// Studio episode editor — the rundown.
//
// A story is a sequence of BEATS, not one block of script. That is what makes
// the standard VO/SOT structure editable: the anchor introduces, hands to a
// clip that plays with its own audio, then tags out.
//
//   anchor_on_cam — anchor talking, no media
//   vo            — anchor talking over muted b-roll
//   sot           — anchor SILENT, clip audio at full volume
//
// Backend endpoints:
//   GET    /studio/episodes/:id                      — full tree
//   POST   /studio/episodes/:id/generate-script      — Claude writes the beats
//   PATCH  /studio/beats/:id                         — edit a beat
//   POST   /studio/segments/:id/beats                — add a beat
//   DELETE /studio/beats/:id                         — remove a beat
//   PATCH  /studio/segments/:id/beats/reorder        — bulk reorder
//   GET    /studio/episodes/:id/rundown              — teleprompter export
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select, Textarea } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

type BeatKind = "anchor_on_cam" | "vo" | "sot";

interface BeatMedia {
  id: string;
  kind: string;
  public_url: string | null;
  credit_label: string | null;
  has_audio: boolean;
}

interface Beat {
  id: string;
  segment_id: string;
  display_order: number;
  kind: BeatKind;
  anchor_script: string | null;
  sot_cue: string | null;
  sot_transcript: string | null;
  duration_estimate_seconds: number | null;
  media: BeatMedia[];
}

interface Segment {
  id: string;
  article_id: string | null;
  display_order: number;
  kind: "cold_open" | "story" | "sign_off";
  lower_third: string | null;
  article_title: string | null;
  beats: Beat[];
}

interface Episode {
  id: string;
  title: string;
  status: string;
  anchor_mode: "avatar" | "obs";
  last_error: string | null;
  /** Set by the publish stage — the rendered master, watchable before air. */
  mux_playback_id: string | null;
  mux_live_stream_id: string | null;
  approved_at: string | null;
  aired_at: string | null;
  duration_seconds: number | null;
}

/** What go-live hands back. The key is a credential and is never re-fetched. */
interface GoLiveResult {
  live_stream_id: string;
  ingest_url: string;
  stream_key: string;
  simulcast: { attached: string[]; skipped: string[] };
  simulcast_error: string | null;
}

const BEAT_LABELS: Record<BeatKind, string> = {
  anchor_on_cam: "Anchor on cam",
  vo: "VO — anchor over muted b-roll",
  sot: "SOT — clip audio, anchor silent",
};

const EDITABLE_STATUSES = ["draft", "scripted", "media_ready", "failed"];

// Kinds that can carry a soundbite. Stock video is essentially always silent
// or music-bedded, so it is VO b-roll only. Mirrors SOT_CAPABLE_KINDS on the
// server, which is where this is actually enforced.
const SOT_CAPABLE_KINDS = ["source_video", "upload", "youtube_embed"];

interface MediaCandidate {
  kind: string;
  source_url: string | null;
  public_url: string | null;
  credit_label: string | null;
  has_audio: boolean;
  origin: string;
  label: string | null;
}

interface ScreenshotTarget {
  url: string;
  label: string;
  credit_label: string | null;
}

export default function StudioEpisodePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [episode, setEpisode] = useState<Episode | null>(null);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingBeat, setSavingBeat] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [picker, setPicker] = useState<{ beat: Beat; segment: Segment } | null>(
    null,
  );
  const [candidates, setCandidates] = useState<MediaCandidate[] | null>(null);
  const [shotTargets, setShotTargets] = useState<ScreenshotTarget[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api<{ episode: Episode; segments: Segment[] }>(
        `/studio/episodes/${id}`,
      );
      setEpisode(r.episode);
      setSegments(r.segments);
    } catch (e: any) {
      setError(e.message ?? "Could not load the episode");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = episode ? EDITABLE_STATUSES.includes(episode.status) : false;

  const [airBusy, setAirBusy] = useState(false);
  const [goLive, setGoLive] = useState<GoLiveResult | null>(null);

  /** One helper for every transmission transition — they differ only in the
   *  endpoint and whether they need confirming. */
  async function transition(
    path: string,
    confirmMessage?: string,
  ): Promise<any | null> {
    if (confirmMessage && !confirm(confirmMessage)) return null;
    setAirBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api<any>(`/studio/episodes/${id}/${path}`, {
        method: "POST",
      });
      await load();
      return r;
    } catch (e: any) {
      setError(e.message ?? `Could not ${path.replace("-", " ")}`);
      return null;
    } finally {
      setAirBusy(false);
    }
  }

  async function generateScript() {
    if (
      !confirm(
        "Generate the rundown? This replaces every existing beat and its attached media.",
      )
    )
      return;

    setGenerating(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api<{ segments: number; beats: number }>(
        `/studio/episodes/${id}/generate-script`,
        { method: "POST" },
      );
      setNotice(`Rundown written — ${r.segments} segments, ${r.beats} beats.`);
      await load();
    } catch (e: any) {
      setError(e.message ?? "Script generation failed");
    } finally {
      setGenerating(false);
    }
  }

  async function saveBeat(beat: Beat, patch: Partial<Beat>) {
    setSavingBeat(beat.id);
    setError(null);
    try {
      await api(`/studio/beats/${beat.id}`, { method: "PATCH", body: patch });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not save the beat");
    } finally {
      setSavingBeat(null);
    }
  }

  async function addBeat(segmentId: string, kind: BeatKind) {
    setError(null);
    try {
      await api(`/studio/segments/${segmentId}/beats`, {
        method: "POST",
        body: {
          kind,
          anchor_script: kind === "sot" ? null : "…",
        },
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not add the beat");
    }
  }

  async function deleteBeat(beatId: string) {
    setError(null);
    try {
      await api(`/studio/beats/${beatId}`, { method: "DELETE" });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not delete the beat");
    }
  }

  // No drag-and-drop anywhere in this app, so reorder is up/down buttons.
  // Send the whole segment's new order so the server can stage it safely.
  async function moveBeat(segment: Segment, index: number, delta: number) {
    const next = [...segment.beats];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];

    setError(null);
    try {
      await api(`/studio/segments/${segment.id}/beats/reorder`, {
        method: "PATCH",
        body: {
          order: next.map((b, i) => ({ id: b.id, display_order: i })),
        },
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not reorder");
    }
  }

  async function openPicker(beat: Beat, segment: Segment) {
    setPicker({ beat, segment });
    setCandidates(null);
    setShotTargets([]);
    if (!segment.article_id) {
      // Cold open and sign-off have no article behind them, so there is
      // nothing to offer — only an upload would make sense here.
      setCandidates([]);
      return;
    }
    try {
      const r = await api<{
        candidates: MediaCandidate[];
        screenshot_targets: ScreenshotTarget[];
      }>(`/studio/articles/${segment.article_id}/media-candidates`);
      setCandidates(r.candidates);
      setShotTargets(r.screenshot_targets);
    } catch (e: any) {
      setError(e.message ?? "Could not load media candidates");
      setCandidates([]);
    }
  }

  async function attachMedia(beat: Beat, body: Record<string, unknown>) {
    setError(null);
    try {
      await api(`/studio/beats/${beat.id}/media`, { method: "POST", body });
      setPicker(null);
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not attach the media");
    }
  }

  async function detachMedia(beatId: string, mediaId: string) {
    setError(null);
    try {
      await api(`/studio/beats/${beatId}/media/${mediaId}`, {
        method: "DELETE",
      });
      await load();
    } catch (e: any) {
      setError(e.message ?? "Could not remove the media");
    }
  }

  const totals = segments.reduce(
    (acc, seg) => {
      for (const b of seg.beats) {
        const s = Number(b.duration_estimate_seconds) || 0;
        acc.total += s;
        if (b.kind === "sot") acc.sot += s;
        else acc.narrated += s;
      }
      return acc;
    },
    { total: 0, narrated: 0, sot: 0 },
  );

  if (loading) return <Spinner label="Loading episode" />;
  if (!episode) {
    return (
      <div>
        <div style={styles.error}>{error ?? "Episode not found"}</div>
        <Button variant="secondary" onClick={() => navigate("/studio")}>
          Back to Studio
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
            {episode.title}
          </h1>
          <CardSubtle>
            {episode.status} ·{" "}
            {episode.anchor_mode === "avatar" ? "AI avatar" : "Human / OBS"}
          </CardSubtle>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={() => navigate("/studio")}>
            Back
          </Button>
          <Button onClick={generateScript} disabled={generating || !editable}>
            {generating ? (
              <Spinner size={12} />
            ) : segments.some((s) => s.beats.length > 0) ? (
              "Regenerate rundown"
            ) : (
              "Generate rundown"
            )}
          </Button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {notice && <div style={styles.notice}>{notice}</div>}
      {episode.last_error && (
        <div style={styles.error}>Last error: {episode.last_error}</div>
      )}

      <TransmissionCard
        episode={episode}
        busy={airBusy}
        goLive={goLive}
        onApprove={() => transition("approve")}
        onUnapprove={() => transition("unapprove")}
        onGoLive={async () => {
          const r = await transition(
            "go-live",
            "Create the live stream and attach the simulcast destinations?\n\n" +
              "Nothing is transmitted yet, but the stream will be visible on " +
              "the destinations as soon as bytes start flowing.",
          );
          if (r) setGoLive(r);
        }}
        onOffAir={async () => {
          const r = await transition(
            "off-air",
            "End the broadcast and close the stream?",
          );
          if (r) setGoLive(null);
        }}
      />

      <Card style={{ marginTop: 16 }}>
        <div style={styles.totalsRow}>
          <Totals
            label="Estimated length"
            value={formatDuration(totals.total)}
          />
          {/* The cost lever: avatar vendors bill narrated minutes only. */}
          <Totals
            label="Narrated (billed)"
            value={formatDuration(totals.narrated)}
          />
          <Totals label="On tape (free)" value={formatDuration(totals.sot)} />
          <Totals
            label="Beats"
            value={String(segments.reduce((n, s) => n + s.beats.length, 0))}
          />
        </div>
      </Card>

      {segments.length === 0 ? (
        <CardSubtle style={{ marginTop: 16 }}>
          No rundown yet — generate one to get started.
        </CardSubtle>
      ) : (
        segments.map((seg) => (
          <Card key={seg.id} style={{ marginTop: 16 }}>
            <div style={styles.segHeader}>
              <div>
                <CardTitle>
                  {seg.kind === "story"
                    ? (seg.article_title ?? "Untitled story")
                    : seg.kind === "cold_open"
                      ? "Cold open"
                      : "Sign-off"}
                </CardTitle>
                {seg.lower_third && (
                  <CardSubtle>Lower third: {seg.lower_third}</CardSubtle>
                )}
              </div>
              <span style={styles.segOrder}>#{seg.display_order + 1}</span>
            </div>

            {seg.beats.map((beat, i) => (
              <div key={beat.id} style={styles.beat}>
                <div style={styles.beatHead}>
                  <Select
                    value={beat.kind}
                    disabled={!editable}
                    onChange={(e) =>
                      saveBeat(beat, { kind: e.target.value as BeatKind })
                    }
                    style={{ maxWidth: 280 }}
                  >
                    {(Object.keys(BEAT_LABELS) as BeatKind[]).map((k) => (
                      <option key={k} value={k}>
                        {BEAT_LABELS[k]}
                      </option>
                    ))}
                  </Select>

                  <div
                    style={{ display: "flex", gap: 4, alignItems: "center" }}
                  >
                    {savingBeat === beat.id && <Spinner size={12} />}
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!editable || i === 0}
                      onClick={() => moveBeat(seg, i, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!editable || i === seg.beats.length - 1}
                      onClick={() => moveBeat(seg, i, 1)}
                    >
                      ↓
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={!editable}
                      onClick={() => deleteBeat(beat.id)}
                    >
                      ✕
                    </Button>
                  </div>
                </div>

                {beat.kind === "sot" ? (
                  <div style={{ marginTop: 8 }}>
                    <Label>Soundbite to find</Label>
                    <Input
                      defaultValue={beat.sot_cue ?? ""}
                      disabled={!editable}
                      placeholder="e.g. Sisi's remark on the dam"
                      onBlur={(e) =>
                        e.target.value !== (beat.sot_cue ?? "") &&
                        saveBeat(beat, { sot_cue: e.target.value })
                      }
                    />
                    <Label style={{ marginTop: 8 }}>Captions</Label>
                    <Textarea
                      defaultValue={beat.sot_transcript ?? ""}
                      disabled={!editable}
                      placeholder="What is said on tape — burned in as captions."
                      onBlur={(e) =>
                        e.target.value !== (beat.sot_transcript ?? "") &&
                        saveBeat(beat, { sot_transcript: e.target.value })
                      }
                    />
                    <CardSubtle>
                      The anchor is silent here and the clip plays at full
                      volume. Needs a clip with audio before this episode can
                      render.
                    </CardSubtle>
                  </div>
                ) : (
                  <div style={{ marginTop: 8 }}>
                    <Textarea
                      defaultValue={beat.anchor_script ?? ""}
                      disabled={!editable}
                      style={{ minHeight: 90 }}
                      onBlur={(e) =>
                        e.target.value !== (beat.anchor_script ?? "") &&
                        saveBeat(beat, { anchor_script: e.target.value })
                      }
                    />
                  </div>
                )}

                <div style={styles.mediaStrip}>
                  {beat.media.length === 0 ? (
                    <span style={styles.mediaEmpty}>
                      {beat.kind === "anchor_on_cam"
                        ? "No media needed"
                        : beat.kind === "sot"
                          ? "Needs a clip with audio"
                          : "No media attached yet"}
                    </span>
                  ) : (
                    beat.media.map((m) => (
                      <span key={m.id} style={styles.mediaChip}>
                        {m.kind}
                        {m.has_audio ? " · audio" : ""}
                        {m.credit_label ? ` · ${m.credit_label}` : ""}
                        {editable && (
                          <button
                            style={styles.chipRemove}
                            title="Remove"
                            onClick={() => detachMedia(beat.id, m.id)}
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    ))
                  )}
                  {editable && beat.kind !== "anchor_on_cam" && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => openPicker(beat, seg)}
                    >
                      + Media
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {editable && (
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                {(Object.keys(BEAT_LABELS) as BeatKind[]).map((k) => (
                  <Button
                    key={k}
                    size="sm"
                    variant="secondary"
                    onClick={() => addBeat(seg.id, k)}
                  >
                    + {k === "anchor_on_cam" ? "Anchor" : k.toUpperCase()}
                  </Button>
                ))}
              </div>
            )}
          </Card>
        ))
      )}

      <Modal
        open={picker !== null}
        onClose={() => setPicker(null)}
        title={
          picker?.beat.kind === "sot"
            ? "Pick the clip that plays"
            : "Pick media for this beat"
        }
      >
        {picker && (
          <MediaPicker
            beat={picker.beat}
            candidates={candidates}
            screenshotTargets={shotTargets}
            onAttach={(body) => attachMedia(picker.beat, body)}
          />
        )}
      </Modal>
    </div>
  );
}

function MediaPicker({
  beat,
  candidates,
  screenshotTargets,
  onAttach,
}: {
  beat: Beat;
  candidates: MediaCandidate[] | null;
  screenshotTargets: ScreenshotTarget[];
  onAttach: (body: Record<string, unknown>) => void;
}) {
  const [manualUrl, setManualUrl] = useState("");

  if (candidates === null) return <Spinner label="Loading media" />;

  const isSot = beat.kind === "sot";
  // A SOT beat is the anchor handing over to tape. Offering a silent still
  // here would just produce dead air, so only sound-carrying media shows up.
  const usable = isSot
    ? candidates.filter(
        (c) => SOT_CAPABLE_KINDS.includes(c.kind) && c.has_audio,
      )
    : candidates;

  return (
    <div>
      {isSot && (
        <CardSubtle style={{ marginBottom: 12 }}>
          The anchor is silent for this beat and the clip plays at full volume,
          so only media that carries audio is listed.
        </CardSubtle>
      )}

      {usable.length === 0 ? (
        <CardSubtle>
          {isSot
            ? "No clip with audio is available for this story. Publisher video comes in through the RSS feed; otherwise upload one you've cleared."
            : "Nothing stored for this story yet."}
        </CardSubtle>
      ) : (
        <div style={styles.candidateList}>
          {usable.map((c, i) => (
            <div key={`${c.source_url}-${i}`} style={styles.candidate}>
              {c.kind === "source_image" && c.public_url && (
                <img src={c.public_url} alt="" style={styles.thumb} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.candidateLabel}>{c.label ?? c.kind}</div>
                <div style={styles.candidateMeta}>
                  {c.kind}
                  {c.has_audio ? " · has audio" : ""}
                  {c.credit_label ? ` · ${c.credit_label}` : " · no credit"}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() =>
                  onAttach({
                    kind: c.kind,
                    source_url: c.source_url,
                    public_url: c.public_url,
                    credit_label: c.credit_label,
                    has_audio: c.has_audio,
                  })
                }
              >
                Use
              </Button>
            </div>
          ))}
        </div>
      )}

      {!isSot && screenshotTargets.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <Label>Screenshot a cited page</Label>
          <CardSubtle>
            Only pages this article already cites can be captured.
          </CardSubtle>
          <div style={styles.candidateList}>
            {screenshotTargets.map((t) => (
              <div key={t.url} style={styles.candidate}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.candidateLabel}>{t.label}</div>
                  <div style={styles.candidateMeta}>{t.url}</div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    onAttach({
                      kind: "screenshot",
                      source_url: t.url,
                      credit_label: t.credit_label,
                      has_audio: false,
                    })
                  }
                >
                  Capture
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <Label>Or paste a URL</Label>
        <div style={{ display: "flex", gap: 8 }}>
          <Input
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
            placeholder="https://…"
          />
          <Button
            variant="secondary"
            disabled={!manualUrl.trim()}
            onClick={() =>
              onAttach({
                kind: isYouTube(manualUrl) ? "youtube_embed" : "upload",
                source_url: manualUrl.trim(),
                has_audio: isSot,
              })
            }
          >
            Attach
          </Button>
        </div>
        <CardSubtle>
          You are responsible for clearing rights on anything pasted here.
          YouTube links are detected automatically and can only be used in OBS
          mode.
        </CardSubtle>
      </div>
    </div>
  );
}

function isYouTube(url: string): boolean {
  try {
    const host = new URL(url).hostname
      .replace(/^www\./, "")
      .replace(/^m\./, "");
    return host === "youtu.be" || host === "youtube.com";
  } catch {
    return false;
  }
}

function Totals({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={styles.totalsLabel}>{label}</div>
      <div style={styles.totalsValue}>{value}</div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Everything between a rendered file and a broadcast.
 *
 * The order is deliberate and one-way: watch it, approve it, provision the
 * stream, transmit, close. Approval is the last reversible step — once go-live
 * has attached the simulcast targets, the next thing that happens is visible on
 * YouTube.
 */
function TransmissionCard({
  episode,
  busy,
  goLive,
  onApprove,
  onUnapprove,
  onGoLive,
  onOffAir,
}: {
  episode: Episode;
  busy: boolean;
  goLive: GoLiveResult | null;
  onApprove: () => void;
  onUnapprove: () => void;
  onGoLive: () => void;
  onOffAir: () => void;
}) {
  const { status } = episode;

  // Nothing to say until there is something to watch.
  if (!["ready", "approved", "airing", "aired"].includes(status)) return null;

  return (
    <Card style={{ marginTop: 16 }}>
      <CardTitle>Transmission</CardTitle>

      {episode.mux_playback_id && (
        <>
          <CardSubtle style={{ marginTop: 8, marginBottom: 8 }}>
            The rendered master. Watch it before approving — this is the file
            that goes out.
          </CardSubtle>
          <div style={styles.playerWrap}>
            <iframe
              title="Rendered master"
              src={`https://player.mux.com/${episode.mux_playback_id}`}
              style={styles.player}
              allow="fullscreen; picture-in-picture"
            />
          </div>
        </>
      )}

      {status === "ready" && (
        <>
          <CardSubtle style={{ marginTop: 12 }}>
            Approving records who signed off. It does not transmit anything, and
            it can be undone.
          </CardSubtle>
          <Button onClick={onApprove} disabled={busy} style={{ marginTop: 10 }}>
            {busy ? "Working…" : "Approve for broadcast"}
          </Button>
        </>
      )}

      {status === "approved" && (
        <>
          <CardSubtle style={{ marginTop: 12 }}>
            Approved
            {episode.approved_at
              ? ` ${new Date(episode.approved_at).toLocaleString()}`
              : ""}
            . Going live creates the Mux stream and attaches your configured
            destinations — they can only be attached while the stream is idle,
            so this happens before anything transmits.
          </CardSubtle>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Button onClick={onGoLive} disabled={busy}>
              {busy ? "Working…" : "Go live"}
            </Button>
            <Button variant="secondary" onClick={onUnapprove} disabled={busy}>
              Un-approve
            </Button>
          </div>
        </>
      )}

      {status === "airing" && (
        <>
          <div style={styles.onAir}>● ON AIR</div>
          {goLive ? (
            <>
              <CardSubtle style={{ marginTop: 10 }}>
                {goLive.simulcast.attached.length > 0
                  ? `Simulcasting to ${goLive.simulcast.attached.join(", ")}.`
                  : "No simulcast destinations configured — this goes to Mux only."}
                {goLive.simulcast.skipped.length > 0 &&
                  ` Skipped (no credentials): ${goLive.simulcast.skipped.join(", ")}.`}
              </CardSubtle>
              {goLive.simulcast_error && (
                <div style={styles.error}>
                  Simulcast problem: {goLive.simulcast_error}
                </div>
              )}

              {episode.anchor_mode === "obs" ? (
                <>
                  <CardSubtle style={{ marginTop: 12 }}>
                    Point OBS at this. The rundown above is your teleprompter.
                  </CardSubtle>
                  <Field label="Server" value={goLive.ingest_url} />
                  <Field label="Stream key" value={goLive.stream_key} secret />
                </>
              ) : (
                <>
                  <CardSubtle style={{ marginTop: 12 }}>
                    Now transmit the master from a terminal. Playout is a
                    long-running process and is deliberately not run by the API —
                    a deploy would take the broadcast off air mid-sentence.
                  </CardSubtle>
                  <pre style={styles.cmd}>pnpm studio:air {episode.id}</pre>
                </>
              )}
            </>
          ) : (
            <CardSubtle style={{ marginTop: 10 }}>
              This episode is airing. The stream key is only shown once, when
              Go live is pressed — if you need it again, take the episode off
              air and provision a fresh stream.
            </CardSubtle>
          )}

          <Button
            variant="secondary"
            onClick={onOffAir}
            disabled={busy}
            style={{ marginTop: 12 }}
          >
            {busy ? "Working…" : "Take off air"}
          </Button>
        </>
      )}

      {status === "aired" && (
        <CardSubtle style={{ marginTop: 12 }}>
          Aired
          {episode.aired_at
            ? ` ${new Date(episode.aired_at).toLocaleString()}`
            : ""}
          .
        </CardSubtle>
      )}
    </Card>
  );
}

/** A copyable value. Secrets stay masked until asked for — this panel sits on
 *  a screen someone might be sharing while they set OBS up. */
function Field({
  label,
  value,
  secret,
}: {
  label: string;
  value: string;
  secret?: boolean;
}) {
  const [shown, setShown] = useState(!secret);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <code style={styles.fieldValue}>
          {shown ? value : "•".repeat(Math.min(value.length, 32))}
        </code>
        {secret && (
          <button
            type="button"
            onClick={() => setShown((v) => !v)}
            style={styles.fieldBtn}
          >
            {shown ? "hide" : "show"}
          </button>
        )}
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText(value)}
          style={styles.fieldBtn}
        >
          copy
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  playerWrap: {
    position: "relative",
    // Capped: a full-width 16:9 player is ~550px tall here and pushes the
    // approve button, which is the point of the panel, below the fold.
    maxWidth: 520,
    paddingTop: "min(56.25%, 293px)",
    borderRadius: "var(--radius-sm)",
    overflow: "hidden",
    background: "#000",
  },
  player: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    border: "none",
  },
  onAir: {
    display: "inline-block",
    marginTop: 12,
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: "#fff",
    background: "var(--danger)",
  },
  cmd: {
    marginTop: 8,
    padding: "8px 10px",
    background: "var(--surface-hover)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    fontFamily: "ui-monospace, monospace",
    fontSize: 12,
    overflowX: "auto",
  },
  fieldLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--text-muted)",
    marginBottom: 3,
  },
  fieldValue: {
    flex: 1,
    minWidth: 0,
    padding: "5px 8px",
    background: "var(--surface-hover)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    fontSize: 12,
    overflowX: "auto",
    whiteSpace: "nowrap",
  },
  fieldBtn: {
    background: "none",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-secondary)",
    fontSize: 11,
    padding: "3px 7px",
    cursor: "pointer",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    gap: 16,
  },
  segHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  segOrder: {
    fontSize: 12,
    color: "var(--text-muted)",
  },
  beat: {
    marginTop: 12,
    padding: 12,
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    background: "var(--bg)",
  },
  beatHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  mediaStrip: {
    marginTop: 8,
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  mediaChip: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 10,
    border: "1px solid var(--border)",
    color: "var(--text-secondary)",
  },
  mediaEmpty: {
    fontSize: 12,
    color: "var(--text-muted)",
  },
  chipRemove: {
    marginLeft: 6,
    background: "none",
    border: "none",
    color: "var(--text-muted)",
    cursor: "pointer",
    fontSize: 11,
    padding: 0,
  },
  candidateList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 8,
  },
  candidate: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 8,
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    background: "var(--bg)",
  },
  thumb: {
    width: 64,
    height: 40,
    objectFit: "cover",
    borderRadius: 3,
    flexShrink: 0,
  },
  candidateLabel: {
    fontSize: 13,
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  candidateMeta: {
    fontSize: 11,
    color: "var(--text-muted)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  totalsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: 16,
  },
  totalsLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--text-muted)",
  },
  totalsValue: {
    fontSize: 20,
    fontWeight: 700,
    color: "var(--text-primary)",
    marginTop: 2,
  },
  error: {
    marginTop: 8,
    fontSize: 13,
    color: "var(--danger)",
  },
  notice: {
    marginTop: 8,
    fontSize: 13,
    color: "var(--success)",
  },
};
